import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import { comum } from '../../../styles/Themes/light';
import { EditavelContainer, ViewModeText, EditModeContainer, ErrorMessage } from '../EditavelCommon/styles';
import { SelectContainer } from '../SelectEditavel/styles';
import api from '../../../services/api';

interface SelectComarcaEditavelProps {
    estado: string | undefined;
    value: string | number | undefined;
    onChange: (value: string, cidade?: string) => void;
    isEditing: boolean;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: string;
    isSearchable?: boolean;
    disabled?: boolean;
    onBlur?: () => void;
}

interface ComarcaOption {
    value: string;
    label: string;
}

const SelectComarcaEditavel: React.FC<SelectComarcaEditavelProps> = ({
    estado,
    value,
    onChange,
    isEditing,
    label,
    placeholder = 'Selecionar',
    required = false,
    error,
    isSearchable = true,
    disabled = false,
    onBlur,
}) => {
    const [comarcas, setComarcas] = useState<ComarcaOption[]>([]);
    const [loading, setLoading] = useState(false);

    const getComarcas = useCallback(async (uf: string) => {
        if (!uf) {
            setComarcas([]);
            return;
        }

        try {
            setLoading(true);
            const response = await api.get(`comarca?uf=${uf}`);
            
            if (response.data.status !== 'success') {
                throw new Error();
            }

            const comarcaArray: ComarcaOption[] = response.data.result.map((row: any) => ({
                label: row.cidade,
                value: String(row.id_comarca)
            }));
            
            setComarcas(comarcaArray);
        } catch (error) {
            console.error('Erro ao buscar comarcas:', error);
            setComarcas([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (estado) {
            getComarcas(estado);
        } else {
            setComarcas([]);
        }
    }, [estado, getComarcas]);

    const selectedOption = useMemo(() => 
        comarcas.find(comarca => comarca.value === String(value)) || null, 
        [comarcas, value]
    );

    const handleChange = useCallback((selectedOption: ComarcaOption | null) => {
        if (selectedOption) {
            onChange(selectedOption.value, selectedOption.label);
        } else {
            onChange('', '');
        }
    }, [onChange]);

    const displayText = useMemo(() => {
        if (!value || !selectedOption) return 'N/A';
        return selectedOption.label;
    }, [value, selectedOption]);

    if (disabled || !isEditing) {
        return (
            <EditavelContainer>
                <ViewModeText>
                    {displayText}
                </ViewModeText>
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </EditavelContainer>
        );
    }

    if (isEditing) {
        return (
            <EditavelContainer>
                <EditModeContainer>
                    <SelectContainer>
                        <Select
                            value={selectedOption}
                            onChange={handleChange}
                            onBlur={onBlur}
                            options={comarcas}
                            placeholder={loading ? 'Carregando...' : placeholder}
                            isSearchable={isSearchable}
                            isDisabled={!estado || loading || disabled}
                            styles={{
                                control: (provided: any, state: any) => ({
                                    ...provided,
                                    minHeight: '32px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    boxShadow: 'none',
                                    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                                    border: error
                                        ? `1px solid ${comum.colors.primaryRed400}`
                                        : '1px solid #EAEEF3',
                                    '&:hover': {
                                        border: error
                                            ? `1px solid ${comum.colors.primaryRed400}`
                                            : `1px solid ${comum.colors.interfaceTextGray}`,
                                    },
                                }),
                                valueContainer: (base) => ({
                                    ...base,
                                    height: '32px',
                                    padding: '0 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'nowrap',
                                    minHeight: '32px',
                                }),
                                input: (base) => ({
                                    ...base,
                                    fontSize: '14px',
                                    margin: 0,
                                    padding: 0,
                                    height: 'auto',
                                    alignSelf: 'center',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: 'var(--c353a40)',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    margin: 0,
                                    lineHeight: '1.2',
                                    alignSelf: 'center',
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: 'var(--c353a40)',
                                    fontSize: '14px',
                                    margin: 0,
                                    lineHeight: '1.2',
                                    padding: 0,
                                    alignSelf: 'center',
                                    position: 'relative',
                                    top: 'auto',
                                    transform: 'none',
                                }),
                                option: (base) => ({
                                    ...base,
                                    color: 'var(--c5a646e)',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    height: '32px',
                                    alignItems: 'center',
                                    alignSelf: 'stretch',
                                }),
                                dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '4px 8px',
                                    alignSelf: 'center',
                                }),
                                indicatorSeparator: () => ({
                                    display: 'none',
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 3,
                                }),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 3,
                                }),
                            }}
                            theme={(theme) => ({
                                ...theme,
                                colors: {
                                    ...theme.colors,
                                    primary25: comum.colors.interfacePure100,
                                    primary50: comum.colors.interfacePure100,
                                    primary: comum.colors.interfacePure100,
                                },
                            })}
                            components={{
                                IndicatorSeparator: () => null,
                            }}
                        />
                    </SelectContainer>
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </EditModeContainer>
            </EditavelContainer>
        );
    }

    return (
        <EditavelContainer>
            <ViewModeText>
                {displayText}
            </ViewModeText>
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </EditavelContainer>
    );
};

export default SelectComarcaEditavel;

